import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

const BASE = "/settings/organization";
const KEY = ["settings", "organization"] as const;

export interface OrganizationDepartment {
  id: number;
  emisor_id: number;
  nombre: string;
  activo: boolean;
  orden: number;
  users_count: number;
}

export interface OrganizationCompany {
  id: number;
  nombre: string;
  cif: string | null;
  users_count: number;
  departments: OrganizationDepartment[];
}

export interface OrganizationUser {
  id: number;
  nombre: string;
  correo: string;
  empresa: string | null;
  departamento: string | null;
  emisor_id: number | null;
  departamento_id: number | null;
}

export interface OrganizationStructure {
  companies: OrganizationCompany[];
  users: OrganizationUser[];
}

export function useOrganization() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await api.get<{ data: OrganizationStructure }>(BASE);
      return data.data;
    },
  });
}

export function useCreateOrganizationCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { nombre: string; cif?: string | null }) => {
      const { data } = await api.post(`${BASE}/companies`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOrganizationCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<{ nombre: string; cif: string | null }>;
    }) => {
      const { data } = await api.patch(`${BASE}/companies/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateOrganizationDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { emisor_id: number; nombre: string }) => {
      const { data } = await api.post(`${BASE}/departments`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOrganizationDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<{ nombre: string; activo: boolean }>;
    }) => {
      const { data } = await api.patch(`${BASE}/departments/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteOrganizationDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/departments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignOrganizationUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      emisor_id,
      departamento_id,
    }: {
      id: number;
      emisor_id: number | null;
      departamento_id: number | null;
    }) => {
      const { data } = await api.patch(`${BASE}/users/${id}`, {
        emisor_id,
        departamento_id,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
